import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlay, FaArrowRight } from 'react-icons/fa';
import { trackSignupFree } from '../utils/analytics.js';
import { setPageSEO } from '../utils/seo.js';
import './Testimonials.css';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerChildren = {
  visible: {
    transition: { staggerChildren: 0.1 }
  }
};

function Testimonials() {
  useEffect(() => setPageSEO({
    title: 'Customer Stories That Build Trust - Clientalio',
    description: 'Turn customer love into social proof that builds credibility, increases trust, and helps more buyers choose your business.',
    canonical: 'https://clientalio.com/testimonials',
    ogTitle: 'Customer Stories That Build Trust - Clientalio',
    ogDescription: 'Make customer love visible so buyers trust you before they talk to you.',
    twitterTitle: 'Customer Stories That Build Trust - Clientalio',
    twitterDescription: 'Turn customer love into social proof that helps more buyers choose your business.'
  }), []);

  const testimonials = [
    {
      id: 1,
      name: "Sarthak Mittal",
      avatar: "S",
      rating: 5,
      text: "Very good tool",
      date: "Jun 14, 2026",
      highlighted: true
    },
    {
      id: 2,
      name: "Kunwar Ateeq Khan",
      avatar: "K",
      rating: 5,
      text: "Clientalio made collecting testimonials incredibly easy. The ability to gather video, screen recorded, and text testimonials in one place helps build trust and showcase genuine customer experiences. Simple, efficient, and valuable for any business looking to strengthen its credibility.",
      date: "Jun 25, 2026",
      highlighted: true
    },
    {
      id: 3,
      name: "Shashwat Saxena",
      role: "CEO",
      rating: 5,
      date: "Jan 14, 2026",
      type: "video"
    },
    {
      id: 4,
      name: "Satyam",
      avatar: "S",
      rating: 5,
      text: "I liked Clientalio, it's easy to use and very helpful. I am going to use it in future for sure.",
      date: "Jan 25, 2026",
      highlighted: true
    },
    {
      id: 5,
      name: "Neha Kapoor",
      avatar: "N",
      rating: 5,
      text: "Clientalio helped us collect customer stories without chasing people over email. The share link is simple, and the final testimonials look clean on our website.",
      date: "Jun 12, 2026",
      highlighted: true
    },
    {
      id: 6,
      name: "JOhn",
      role: "CEO",
      rating: 4,
      date: "Jan 15, 2026",
      type: "video"
    },
    {
      id: 7,
      name: "Ayush Bhardwaj",
      avatar: "A",
      rating: 5,
      text: "It was good using the platform",
      date: "Jun 14, 2026",
      highlighted: true
    },
    {
      id: 8,
      name: "Priya Menon",
      role: "Founder",
      avatar: "P",
      rating: 5,
      text: "We started getting better replies from clients once we switched to Clientalio. The collection page feels professional and saves our team a lot of manual follow-up.",
      date: "Jun 10, 2026",
      highlighted: true
    },
    {
      id: 9,
      name: "Ravi Sharma",
      avatar: "R",
      rating: 5,
      text: "Excellent experience. The team was professional, responsive, and supportive throughout the process. Everything was handled efficiently and clearly. Highly recommended.",
      date: "Jan 14, 2026",
      highlighted: true
    },
    {
      id: 10,
      name: "Amit Verma",
      role: "Agency Owner",
      avatar: "A",
      rating: 5,
      text: "The dashboard is straightforward and our clients understand exactly what to do. We now have fresh reviews ready for landing pages every week.",
      date: "Jun 8, 2026",
      highlighted: true
    },
    {
      id: 11,
      name: "Meera Iyer",
      role: "Marketing Lead",
      avatar: "M",
      rating: 5,
      text: "Adding video and text testimonials in one place made our campaign pages more trustworthy. Setup was fast and the widgets matched our brand nicely.",
      date: "Jun 6, 2026",
      highlighted: true
    },
    {
      id: 12,
      name: "Rahul Singh",
      role: "Consultant",
      avatar: "R",
      rating: 5,
      text: "Clientalio gave me a simple way to collect proof from happy customers after every project. It feels polished, and the embed works well on my site.",
      date: "Jun 4, 2026",
      highlighted: true
    }
  ];

  const featuredTestimonials = testimonials.slice(0, 9);

  return (
    <div className="testimonials-page">
      {/* Hero */}
      <motion.section 
        className="testimonials-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container testimonials-hero-inner">
          <motion.div
            className="testimonials-hero-copy"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
          >
            <span className="testimonials-eyebrow">REAL STORIES. REAL CUSTOMER PROOF.</span>
            <h1>Your Customers Are Your Best Sales Team</h1>
            <p>Real customer stories build credibility faster than another marketing claim from your brand.</p>
            <div className="testimonials-hero-actions">
              <button
                className="testimonials-primary-action"
                onClick={() => { trackSignupFree('testimonials_hero'); window.location.href = '/signup'; }}
              >
                Collect your first story <FaArrowRight />
              </button>
              <a href="#customer-stories" className="testimonials-secondary-action">See customer stories</a>
            </div>
          </motion.div>
          <motion.aside className="testimonials-hero-proof" initial={{ opacity: 0, x: 26 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.65 }}>
            <span>TRUST AT A GLANCE</span>
            <strong>4.9</strong>
            <div className="testimonials-hero-stars">★★★★★</div>
            <p>Customer stories collected across video and text.</p>
            <div><b>100+</b><small>beta signups</small></div>
            <div><b>98%</b><small>satisfaction rate</small></div>
          </motion.aside>
        </div>
      </motion.section>

      {/* Featured Testimonials */}
      <motion.section 
        className="featured-testimonials"
        id="customer-stories"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>Customer Proof People Can Believe</h2>
          </motion.div>
          <motion.div 
            className="featured-grid"
            variants={staggerChildren}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {featuredTestimonials.map((testimonial) => (
              <motion.div 
                key={testimonial.id} 
                className={`featured-card${testimonial.type === 'video' ? ' video-featured-card' : ''}`}
                variants={fadeInUp}
                whileHover={{ y: -8 }}
              >
                {testimonial.type === 'video' && (
                  <div className="featured-video-preview">
                    <button className="featured-video-play" type="button" aria-label={`Play ${testimonial.name} testimonial`}>
                      <FaPlay />
                    </button>
                  </div>
                )}
                <div className="featured-card-body">
                  {testimonial.type !== 'video' && <span className="featured-heart" aria-hidden="true">❤</span>}
                  <div className="testimonial-header">
                    {testimonial.type !== 'video' && (
                      <div className="testimonial-avatar-large">{testimonial.avatar}</div>
                    )}
                    <div className="testimonial-info">
                      <h3>{testimonial.name}</h3>
                      {testimonial.role && <p className="role">{testimonial.role}</p>}
                    </div>
                  </div>
                  {testimonial.text && <p className="testimonial-text">{testimonial.text}</p>}
                  {testimonial.text && <p className="testimonial-reviewer">— Reviews by {testimonial.name}</p>}
                  <div className="testimonial-rating">
                    <span>{'★'.repeat(testimonial.rating)}{'☆'.repeat(5 - testimonial.rating)}</span>
                    <strong>{testimonial.rating}.0</strong>
                  </div>
                  <div className="testimonial-footer">
                    <span className={testimonial.highlighted ? 'testimonial-highlight' : ''}>⚐ Highlight</span>
                    <span>⌘</span>
                    <span>↗</span>
                    <time>{testimonial.date}</time>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Stats */}
      <motion.section 
        className="testimonials-stats"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div 
            className="stats-grid"
            variants={staggerChildren}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div className="stat-card" variants={fadeInUp}>
              <div className="stat-number">4.7 k+</div>
              <p> Our Community Views</p>
            </motion.div>
            <motion.div className="stat-card" variants={fadeInUp}>
              <div className="stat-number">100+</div>
              <p>Beta Signups</p>
            </motion.div>
            <motion.div className="stat-card" variants={fadeInUp}>
              <div className="stat-number">98%</div>
              <p>Satisfaction Rate</p>
            </motion.div>
            <motion.div className="stat-card" variants={fadeInUp}>
              <div className="stat-number">24/7</div>
              <p>Customer Support</p>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section 
        className="testimonials-cta"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2>Start Turning Customer Love Into Future Customers</h2>
            <p>Create your branded trust page, share your first request, and make customer proof visible in under 2 minutes.</p>
            <div className="cta-actions">
              <button
                className="btn-primary"
                onClick={() => { trackSignupFree('testimonials_cta'); window.location.href = '/signup'; }}
              >
                Start FREE In Under 2 Minutes
                <FaArrowRight style={{ marginLeft: '10px' }} />
              </button>
              {/* <button className="btn-secondary">View More Examples</button> */}
            </div>
            <p className="cta-trust">✓ Branded page  ✓ First customer request  ✓ No credit card required</p>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}

export default Testimonials;
